/**
 * Hotel Policy Rules Thunk
 *
 * Fetches hotel policy rules
 * POST /hotels/policy-rules
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import type { HotelRulesData } from '@contracts';
import {
  fetchHotelPolicyRules as fetchHotelPolicyRulesApi,
  HotelPolicyRulesError,
} from '../../api';
import type { RootState, AppDispatch } from '@/store/store';
import { logger } from '@/shared/utils/logger';
import { getOrCreateSessionId } from '@/shared/utils';
import { getUUID } from '@/shared/utils/appCookies';

// ============================================
// Types
// ============================================

interface FetchHotelPolicyRulesParams {
  /** Transaction key from booking review response */
  txnKey: string;
}

interface FetchHotelPolicyRulesResult {
  rulesData: HotelRulesData;
}

// ============================================
// Thunk
// ============================================

/**
 * Fetch hotel policy rules
 * POST /hotels/policy-rules
 */
export const fetchHotelPolicyRulesThunk = createAsyncThunk<
  FetchHotelPolicyRulesResult,
  FetchHotelPolicyRulesParams,
  { state: RootState; dispatch: AppDispatch; rejectValue: string }
>(
  'hotels/fetchPolicyRules',
  async (params, { rejectWithValue }) => {
    try {
      const sessionId = getOrCreateSessionId();
      const policyRulesResponse = await fetchHotelPolicyRulesApi({
        sessionId,
        userId: getUUID(),
        txnKey: params.txnKey,
      });

      return {
        rulesData: policyRulesResponse.data,
      };
    } catch (error) {
      logger.error('[PolicyRulesThunk] Failed to fetch hotel policy rules:', error);
      if (error instanceof HotelPolicyRulesError) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to fetch hotel policy rules');
    }
  },
  {
    // Prevent duplicate API calls - condition runs BEFORE pending action is dispatched
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      const { isPolicyRulesLoading } = state.hotels.bookingReview;
      // Return false to cancel the thunk if already loading
      return !isPolicyRulesLoading;
    },
  },
);
